import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Box, Grid, Typography, styled } from '@mui/material'
import { Loading } from '../components/ui'
import { TrackingOrderToolbar, TrackingHeader, TrackingTimeline, TrackingEvents, TrackingData, TrackingChildrenOrders, TrackingChildrenErrors, CourierLink, NotFound, NotFoundData } from '../scenes'
import { CourierProps } from '../types/default'
import { RequestTrackingByOrderParams, RequestTrackingByOrderResponse, RequestTrackingByChildrenResponse, RequestTrackingLoaderResponse } from '../types/tracking'
import { findCourier } from '../utils/utilities'
import { translate } from '../utils/resources'
import { trackingService } from '../services'
import DEFAULT from '../config/default'
import orderMock from '../config/order-mock'

const countryCode: string | undefined = DEFAULT.api.countryCode

const loader = async (params: RequestTrackingByOrderParams): Promise<RequestTrackingLoaderResponse> => {
    try {
        const parent = await trackingService.byOrder(params) as RequestTrackingByOrderResponse
        if (parent.pedido !== null) {
            if (
                DEFAULT.api.countryCode === 'AR'
                ||
                DEFAULT.api.countryCode === 'MX'
            ) {
                if (parent.pedido.tieneHijos) {
                    try {
                        const children = await trackingService.byChildren({ id: parent.pedido.id }) as RequestTrackingByChildrenResponse
                        return { parent, children }
                    } catch (error) {
                        return { parent }
                    }
                }
            }
            return { parent }
        } else {

            // this is a workaround to show the epg in the timeline
            if (parent.epg) {
                return { parent: {
                    ...orderMock(parent.epg, params),
                    epg: parent.epg
                }}
            }

            return { parent: undefined }
        }
    } catch (error) {
        return { parent: undefined }
    }
}

const PageTracking = () => {

    const params = useParams()

    const [data, setData] = useState<RequestTrackingLoaderResponse | undefined>(undefined)
    const [courier, setCourier] = useState<CourierProps | undefined>()
    const [stopEvent, setStopEvent] = useState<boolean>(false)

    useEffect(() => {
        let active = true;
        (async () => {
            setData(undefined)
            setCourier(undefined)
            setStopEvent(false)
            if (params.id && params.id.length >= 8) {
                const request = await loader({ id: params.id as string }) as RequestTrackingLoaderResponse
            
                if (!active) {
                    return
                }

                if (request.parent) {
                    if (request.parent.pedido.eventoTipoFinalizacionTracking) {
                        setStopEvent(true)
                    } else {
                        const find = findCourier(request.parent.pedido)
                        if (find) {
                            setCourier(find)
                        }
                    }
                }
                
                setData(request)

            } else {
                setData({ parent: undefined })
            }
        })()
        return () => {
            active = false
        }
    }, [params])

    return (
        <LayoutStyled>
            { data ? (
                data.parent ? (
                    !stopEvent ? ( 
                    <>

                        <TrackingOrderToolbar
                            order={data.parent.pedido} 
                        />

                        <TrackingHeader
                            action={
                                <CourierLink data={data.parent} courier={courier} />
                            }
                        />

                        <TrackingTimeline
                            parent={data.parent}
                            children={data.children}
                        />

                        <TrackingCourierLinkStyled>
                            <CourierLink data={data.parent} courier={courier} />
                        </TrackingCourierLinkStyled>

                        <MainStyled>
                            <Grid container spacing={2}>
                                <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
                                    <TrackingEvents events={data.parent.listEstadoPedido} epg={data.parent.epg} />
                                </Grid>
                                <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
                                    <TrackingData order={data.parent.pedido} />
                                </Grid>
                            </Grid>
                        </MainStyled>

                        <TrackingChildrenOrders
                            children={data.children}
                            courier={courier}
                        />

                        {countryCode === 'MX' && (
                            <TrackingChildrenErrors
                                parent={data.parent}
                                children={data.children}
                            />
                        )}

                    </>
                    ) : (
                        <NotFound type="stopEvent">
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography color="primary" variant="h5" component="p" gutterBottom>
                                    {translate('order.stop.event.title')}
                                </Typography>
                                { data.parent.pedido.idPedidoReemplazante == null ? (
                                    <Typography>
                                        {translate('order.stop.event.replacement')}
                                    </Typography>
                                ) : (
                                    <Typography>
                                        Su nuevo pedido es el <strong>{ data.parent.pedido.idPedidoReemplazante }</strong>
                                    </Typography>
                                )}
                                <Typography dangerouslySetInnerHTML={{__html: translate('order.stop.event.thanks')}} />
                            </Box>
                        </NotFound>
                    )
                ) : (
                    <NotFoundData>
                        { translate('order.notfound.message').replace('{id}', String(params.id ?? '')) }
                    </NotFoundData>
                )
            ) : (
                <Loading
                    message={
                        <>
                            Un momento por favor,
                            <br/>
                            buscando información del pedido <strong>{ params.id }</strong>
                        </>
                    } 
                />
            )}
        </LayoutStyled>
     )

}

const LayoutStyled = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
}))

const MainStyled = styled(Box)(({ theme }) => ({
    [theme.breakpoints.down('md')]: {
        marginTop: theme.spacing(2)
    }
}))

const TrackingCourierLinkStyled = styled(Box)(({ theme }) => ({
    [theme.breakpoints.up('md')]: {
        display: 'none'
    }
}))


export default PageTracking
export { loader }
